/**
 * ChatHeaderBanner displays the Ask Hajra portrait as a dignified, collapsible banner above the chat header.
 */
import React, { useState } from 'react';
import { useLocaleNavigate } from '@lib/i18n/exports';
import { motion } from 'motion/react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '../../../../lib/utils';

interface ChatHeaderBannerProps {
  className?: string;
}

export const ChatHeaderBanner: React.FC<ChatHeaderBannerProps> = ({ className }) => {
  const navigate = useLocaleNavigate();
  const [isCollapsed, setIsCollapsed] = useState(true);

  const handleToggle = (): void => {
    setIsCollapsed((prev) => !prev);
  };

  const handleNavigate = (): void => {
    navigate('/about-ask-hajra');
  };

  return (
    <section
      className={cn(
        'relative bg-neutral-50 dark:bg-gray-900',
        className
      )}
      aria-label="Ask Hajra introduction"
    >
      <div className="flex items-center justify-between px-4 py-2">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-neutral-900 dark:text-gray-100">
            Ask Hajra
          </p>
          <p className="text-xs text-neutral-500 dark:text-gray-400">
            Warm, steady guidance in every conversation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleNavigate}
            className="hidden md:inline-flex items-center gap-1 rounded-full border border-neutral-200/80 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-2 text-xs font-semibold text-neutral-800 dark:text-gray-100 shadow-xs hover:border-neutral-300 hover:text-neutral-900 focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-neutral-50 dark:focus:ring-offset-gray-900"
            aria-label="Go to About page"
          >
            About
          </button>
          <button
            type="button"
            onClick={handleToggle}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200/80 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-2 text-xs font-semibold text-neutral-800 dark:text-gray-100 shadow-xs hover:border-neutral-300 hover:text-neutral-900 focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-neutral-50 dark:focus:ring-offset-gray-900"
            aria-label={isCollapsed ? 'Show' : 'Hide'}
            aria-expanded={!isCollapsed}
            aria-controls="chat-hajra-banner"
          >
            <span>{isCollapsed ? 'Show' : 'Hide'}</span>
            {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <motion.button
        id="chat-hajra-banner"
        type="button"
        onClick={handleNavigate}
        initial={false}
        animate={{
          height: isCollapsed ? 0 : 'auto',
          opacity: isCollapsed ? 0 : 1
        }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        className={cn(
          'group relative block w-full overflow-hidden focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-neutral-50 dark:focus:ring-offset-gray-900',
          isCollapsed && 'pointer-events-none'
        )}
        aria-label="Learn more about Ask Hajra"
      >
        {!isCollapsed && (
          <div className="flex flex-col items-center gap-2 px-3 pb-3 pt-2">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {['Ask Hajra', 'Warm, wise, dignified'].map((label) => (
              <span
                key={label}
                className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-white/85 px-3.5 py-2 text-[11px] font-semibold text-emerald-800 shadow-xs ring-1 ring-emerald-100/70 backdrop-blur-sm transition-transform duration-200 group-hover:-translate-y-px group-focus-visible:-translate-y-px group-hover:shadow-md dark:bg-gray-900/80 dark:text-emerald-100 dark:ring-gray-700"
              >
                {label}
              </span>
            ))}
          </div>
          <p className="text-[11px] font-medium text-emerald-900/80 transition-opacity duration-200 group-hover:opacity-100 dark:text-emerald-100/80">
            Practical, respectful guidance to help you use AmalQ with confidence.
          </p>

          <div className="relative w-full max-w-[95%] overflow-hidden rounded-2xl border border-emerald-200/80 bg-white/90 shadow-xs ring-1 ring-emerald-100/70 backdrop-blur-sm transition-transform duration-200 group-hover:translate-y-[-2px] group-hover:shadow-lg group-focus-visible:translate-y-[-2px] dark:border-gray-800 dark:bg-gray-900/80 dark:ring-gray-800 md:max-h-[400px] lg:max-h-[420px]">
            <div className="absolute inset-0 bg-linear-to-b from-white/30 via-white/10 to-white/5 dark:from-gray-900/35 dark:via-gray-900/15 dark:to-gray-900/10 pointer-events-none" />
            <div className="relative aspect-4/5 md:aspect-5/4 lg:aspect-4/3 w-full">
              <img
                src="/images/chat/hajra-header.jpg"
                alt="Portrait of Hajra representing Ask Hajra"
                className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-200 group-hover:scale-[0.985] group-focus-visible:scale-[0.985]"
                loading="eager"
              />
            </div>
          </div>
        </div>
      )}
    </motion.button>

      {/* Guidance block - shown when expanded, positioned at bottom */}
      {!isCollapsed && (
        <div className="px-4 pb-3 -mb-px">
          <div className="w-full max-w-[92%] mx-auto rounded-2xl bg-white/92 p-3 text-left shadow-xs ring-1 ring-emerald-100/70 backdrop-blur-sm dark:bg-gray-900/85 dark:ring-gray-800">
            <p className="text-[12px] font-semibold text-emerald-900 dark:text-emerald-100">
              Guidance grounded in Islamic ethics.
            </p>
            <p className="mt-1 text-[11px] leading-relaxed text-gray-700 dark:text-gray-200">
              A calm, maternal assistant who keeps every exchange clear, respectful, and steady.
            </p>
          </div>
        </div>
      )}
  </section>
  );
};

